import { Modal, Typography, message } from 'antd';
import { Customer } from '@/libs/fleet-api/services/customer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteCustomer } from '@/libs/fleet-api/services/admin';
import { useState } from 'react';

export type DeleteCustomerModalData = {
	customer: Customer;
};

export const useDeleteCustomerModal = () => {
	const [modalState, setModalState] = useState<{
		isOpen: boolean;
		data?: DeleteCustomerModalData;
	}>({ isOpen: false });

	return {
		modal: modalState,
		open: (data: DeleteCustomerModalData) =>
			setModalState({ isOpen: true, data }),
		close: () => setModalState({ isOpen: false }),
		data: modalState.data,
	};
};

export default function DeleteCustomerModal({
	modal,
	data,
	onClose,
}: {
	modal: ReturnType<typeof useDeleteCustomerModal>['modal'];
	data?: DeleteCustomerModalData;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: deleteCustomer,
		onSuccess: () => {
			message.success('Customer berhasil dihapus');
			queryClient.invalidateQueries({ queryKey: ['getAdminCustomers'] });
			onClose();
		},
		onError: (error: Error) => {
			message.error(error?.message || 'Gagal menghapus customer');
		},
	});

	const handleOk = () => {
		if (!data?.customer?.customerId) return;
		mutation.mutate({ customerId: data.customer.customerId });
	};

	return (
		<Modal
			title="Delete Customer"
			open={modal.isOpen}
			onOk={handleOk}
			onCancel={onClose}
			confirmLoading={mutation.isPending}
			okText="Yes, Delete"
			cancelText="Cancel"
			okButtonProps={{ danger: true }}
		>
			<Typography.Text>
				Are you sure you want to delete this customer? This action cannot be undone.
				<br />
				<strong>Customer Name:</strong> {data?.customer?.customerName}
				<br />
				<strong>Customer ID:</strong> {data?.customer?.customerId}
			</Typography.Text>
		</Modal>
	);
}
